import { Pagination, ViewMorePopover } from '@/components/ats';
import { TableRowActions } from '@/components/ats/Table/components';
import { jobStatusConfig } from '@/constants/job-constants';
import { atsJobsActions, getAtsJobs } from '@/store/atsJobs';
import { Box, Flex, Icon, Tooltip } from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';
import { DotsHorizontal } from '@kula-ai/icons/outline/DotsHorizontal';
import { jobsTableRowConfig } from '../utils';
import { buildJobCardRedirectLink } from '../utils/jobCardHelpers';
import { Users01 } from '@kula-ai/icons/outline/Users01';
import { jobsEmploymentsOptions } from '@/constants/job-constants';
import { memo, useCallback, useRef, useState } from 'react';
import { useHistory, Link } from 'react-router-dom';
import { Time } from '@/ui/Time';
import { useShowToast } from '@/hooks';
import { fetchJobsWithFilter } from '../utils/jobsHelpers';
import { TruncatedTextWithTooltip } from '@/ui/TooltipText';
import { useAtsPermissions } from '@/hooks/useAtsPermissions';
import { KText } from '@/ui-library/components/text';
import { getTooltipLabel } from '@/utils/jobBoardTooltip';
import { useContainerWidth } from '@/hooks/use-container-width';
import { ATS_MILESTONES } from '@/ui-library/components/constants';
import { ScrollArrow } from './ScrollArrow';
import { useScrollArrows } from './useScrollArrows';

// Hover scroll arrows on the stage row are parked for now — flip to `true` to
// bring them back. The component, the hook and their tests all stay in place,
// so re-enabling is this one line.
const SHOW_STAGE_SCROLL_ARROWS = false;

// Lives at module scope: declared inside RenderJobCard it was a new component
// type every render, so React remounted every cell on each card render.
const JobStageCell = ({
  item = {},
  jobId,
  jobState,
  cellPadding,
  stageWidth,
  wrapperProps = {},
  textProps = {},
}) => {
  const history = useHistory();
  const {
    application_count = 0,
    name = '',
    id: jobStageId,
    pos,
    firstProspectId,
  } = item;

  // Use firstProspectId for navigation if this is a merged prospect stage, otherwise use the regular jobStageId
  const navigationStageId =
    jobStageId === 'prospect-merged' && firstProspectId
      ? firstProspectId
      : jobStageId;

  const redirectionLink = buildJobCardRedirectLink(
    jobState,
    jobId,
    navigationStageId
  );

  return (
    <Flex
      flexDir="column"
      borderRight={pos !== 'last' && '1px solid #EBEBEB'}
      p={cellPadding}
      // Candidates is the fixed leading cell; the stages share the rest.
      // Keyed off `pos`, not `index` — the leading cell is rendered without
      // an index, so `index !== 0` sized it to grow and pinned stage one.
      flex={pos === 'first' ? 'none' : '1'}
      alignItems={pos === 'first' ? 'start' : 'center'}
      justifyContent="space-between"
      onClick={() => history.push(redirectionLink)}
      data-test-id="job-card-cell"
      _hover={{
        cursor: 'pointer',
        background: jobStageId !== 'leads' && 'primary-25',
      }}
      {...wrapperProps}
    >
      <KText
        size={pos === 'first' ? 'xl-medium' : 'sm-semi-bold'}
        textAlign={pos === 'first' ? 'start' : 'center'}
        pt={pos !== 'first' && '8px'}
        color="secondary-900"
        {...textProps}
      >
        {application_count}
      </KText>
      <Flex alignItems="center" gap=".25em">
        {pos === 'first' && (
          <Icon as={Users01} h="12px" w="12px" color="secondary-700" />
        )}
        <TruncatedTextWithTooltip
          // The leading cell is content-sized, so pinning its label to the
          // stage width truncated "Candidates in this job" with room to spare.
          maxW={pos === 'first' ? undefined : `${stageWidth}px`}
          toolTipVariant="default"
          size="sm-medium"
          width={pos === 'first' ? 'max-content' : `${stageWidth}px`}
          textAlign={pos === 'first' ? 'start' : 'center'}
          color="secondary-700"
          text={name}
          enableTooltip={true}
          {...textProps}
        />
      </Flex>
    </Flex>
  );
};

const RenderJobCard = memo(
  ({ job, containerWidth, refetchJobs = () => {} }) => {
    const {
      id,
      name,
      state,
      confidential,
      employment_type,
      department = null,
      offices = [],
      // open_at,
      created_at,
      application_count: totalCount,
      stages = [],
      kind,
      job_post_kind,
      job_post_listed,
      job_post_internal_option,
      rejected_applications_count = 0,
    } = job;
    const history = useHistory();
    const toast = useShowToast();
    const [isHovered, setIsHovered] = useState(false);
    const { color, label } = jobStatusConfig[state] || {};
    const dispatch = useDispatch();
    const employmentLabel = jobsEmploymentsOptions.find(
      (i) => i?.value === employment_type
    )?.label;
    const type = 'card';
    const formRef = useRef();
    const { isJobCreateAllowed } = useAtsPermissions();
    const stagesRef = useRef(null);
    const { canScrollLeft, canScrollRight, scrollByPage } =
      useScrollArrows(stagesRef);

    // Narrow cards fit more stages per screen with a smaller floor and gutter.
    // Keyed off the measured card, not the viewport, so the row stays right
    // wherever the card is dropped.
    const isNarrow = containerWidth < 768;
    const cellPadding = isNarrow ? '0.875em 0.75em' : '1.063em 1.875em';

    const stagesCount = stages?.length + 2; // +2 for total candidates and rejected
    const gutter = isNarrow ? 24 : 60;
    const cardWidth = containerWidth - (gutter * stagesCount + 100);
    const MIN_STAGE_WIDTH = isNarrow ? 68 : 100;
    const stageWidth =
      stagesCount === 2
        ? MIN_STAGE_WIDTH
        : Math.max(
            MIN_STAGE_WIDTH,
            cardWidth > 0 ? cardWidth / stagesCount : MIN_STAGE_WIDTH
          );

    const cellProps = { jobId: id, jobState: state, cellPadding, stageWidth };

    const sectionOne = () => {
      const topContent = () => {
        return (
          // The badges sit beside the title on a wide card and drop beneath it
          // on a narrow one, where sharing the row wrapped the title to a
          // four-line column.
          <Flex
            alignItems="center"
            flexWrap={isNarrow ? 'wrap' : 'nowrap'}
            rowGap="6px"
          >
            <KText
              size="md-semi-bold"
              textTransform="capitalize"
              color={isHovered ? 'primary-500' : 'secondary-900'}
              wordBreak="break-word"
            >
              {name}
            </KText>
            {isNarrow && (
              <Flex ml="auto" pl="8px" flexShrink={0}>
                {sectionTwo()}
              </Flex>
            )}
            <Flex
              ml={isNarrow ? 0 : '1em'}
              gap="6px"
              flexBasis={isNarrow ? '100%' : undefined}
            >
              {kind === 'sample' && (
                <Flex
                  h="24px"
                  p="0.125em 0.5em"
                  alignItems="center"
                  borderRadius="6px"
                  bg="#FFFAEB"
                  color="#B54708"
                >
                  <KText size="sm-medium" color="inherit">
                    Sample
                  </KText>
                </Flex>
              )}
              <Flex
                h="24px"
                p="2px 6px"
                border="1px solid"
                alignItems="center"
                borderRadius="6px"
                borderColor="border-color"
              >
                <Box
                  w="8px"
                  h="8px"
                  bgColor={color}
                  borderRadius="full"
                  mr="3px"
                />
                <KText
                  size="sm-medium"
                  color="secondary-700"
                  lineHeight="1"
                  display="inline-block"
                >
                  {label}
                </KText>
              </Flex>
              {confidential && (
                <Flex
                  h="24px"
                  p="0.125em 0.5em"
                  bgColor="secondary-50"
                  borderRadius="6px"
                  color="secondary-700"
                  alignItems="center"
                >
                  <KText size="sm" color="inherit">
                    Confidential
                  </KText>
                </Flex>
              )}
              <>
                {job_post_listed && state === 'published' && (
                  <Tooltip
                    label={getTooltipLabel(
                      job_post_internal_option,
                      job_post_kind
                    )}
                    placement="top"
                    hasArrow
                    variant="default"
                    bg="ats-secondary"
                    color="secondary-700"
                    borderRadius="md"
                    fontSize="sm"
                  >
                    <Flex
                      h="24px"
                      p="0.125em 0.5em"
                      alignItems="center"
                      borderRadius="6px"
                      bg="#ECFDF3"
                      color="#027A48"
                      cursor="pointer"
                    >
                      <KText size="sm" fontWeight={450} color="inherit">
                        Listed
                      </KText>
                    </Flex>
                  </Tooltip>
                )}

                {job_post_listed === false && state === 'published' && (
                  <Flex
                    h="24px"
                    p="0.125em 0.5em"
                    alignItems="center"
                    borderRadius="6px"
                    bg="pastel-1-light"
                    color="pastel-1-dark"
                  >
                    <KText size="sm" fontWeight={450} color="inherit">
                      Unlisted
                    </KText>
                  </Flex>
                )}
              </>
            </Flex>
          </Flex>
        );
      };

      const bottomContent = () => {
        return (
          // Wrap whole entries rather than letting each squeeze and break
          // mid-word ("Customer / Success").
          <Flex
            gap=".5em"
            alignItems="center"
            flexWrap={isNarrow ? 'wrap' : 'nowrap'}
            rowGap="4px"
          >
            {department && (
              <>
                <KText
                  fontSize="sm"
                  color="secondary-700"
                  textTransform="capitalize"
                  // Ellipsise rather than wrap on a narrow card: wrapping left a
                  // ragged half-empty column and pushed the rest onto its own
                  // line. minW lets it shrink at all inside the row.
                  {...(isNarrow && {
                    isTruncated: true,
                    enableTooltip: true,
                    minW: 0,
                  })}
                >
                  {department}
                </KText>
                <Box
                  w="2px"
                  h="2px"
                  bgColor={'ats-text-secondary'}
                  borderRadius="10px"
                ></Box>
              </>
            )}
            <ViewMorePopover
              list={offices}
              emptyMsg=""
              renderInitial={(list) =>
                list.map((i, ind) => (
                  <KText key={ind} fontSize="sm" color="secondary-700">
                    {i}
                  </KText>
                ))
              }
              renderOthers={() => (
                <Flex flexDir="column">
                  {offices?.map((i, ind) => (
                    <Flex key={ind} alignItems="center" gap=".25em">
                      <KText key={i?.label} fontSize="sm" color="secondary-700">
                        {i}
                      </KText>
                    </Flex>
                  ))}
                </Flex>
              )}
              popOverStyles={{
                maxW: '15em',
              }}
              moreTextStyles={{
                size: 'sm',
                color: 'secondary-700',
                _hover: { color: 'primary-500' },
              }}
            />

            {employmentLabel && (
              <>
                <Box
                  w="2px"
                  h="2px"
                  bgColor={'ats-text-secondary'}
                  borderRadius="10px"
                />
                <KText fontSize="sm" color="secondary-700">
                  {employmentLabel}
                </KText>
              </>
            )}
          </Flex>
        );
      };

      return (
        <Flex
          gap=".5em"
          flexDir="column"
          minW={0}
          flex={isNarrow ? '1' : undefined}
        >
          {topContent()}
          {bottomContent()}
        </Flex>
      );
    };

    const sectionTwo = () => {
      return (
        <Flex gap="1em" flexShrink={0}>
          <Time
            time={created_at}
            p="0.125em 0.5em"
            fontSize="sm"
            bgColor="secondary-50"
            h="fit-content"
            whiteSpace="nowrap"
            borderRadius="6px"
            color="secondary-700"
            isAts
          />
          <TableRowActions
            actionComp={() => (
              <Icon
                as={DotsHorizontal}
                color="primary-900"
                width="20px"
                height="20px"
              />
            )}
            actionItems={jobsTableRowConfig(
              history,
              job,
              toast,
              dispatch,
              type,
              formRef,
              refetchJobs,
              isJobCreateAllowed,
              true
            )}
          />
        </Flex>
      );
    };

    const redirectLink = `/jobs/${id}${
      state !== 'draft' ? '' : '?tab=jobInformation'
    }`;

    const mergeLeadStages = (stages) => {
      const leadStages = stages.filter(
        (stage) => stage.ats_job_milestone?.name === ATS_MILESTONES.LEAD
      );

      const otherStages = stages.filter(
        (stage) => stage.ats_job_milestone?.name !== ATS_MILESTONES.LEAD
      );

      if (leadStages.length === 0) return stages;

      // Sort lead stages by position to get the first (lowest position) stage
      const sortedLeadStages = leadStages.sort(
        (a, b) => a.position - b.position
      );
      const firstLeadStage = sortedLeadStages[0];

      const prospectStage = {
        id: 'prospect-merged',
        firstProspectId: firstLeadStage?.id, // Store the first prospect stage ID for navigation
        name: firstLeadStage?.name,
        position: firstLeadStage?.position,
        ats_job_milestone: { name: ATS_MILESTONES.LEAD },
        application_count: leadStages.reduce(
          (acc, curr) => acc + (curr.application_count || 0),
          0
        ),
      };

      return [prospectStage, ...otherStages].sort(
        (a, b) => a.position - b.position
      );
    };

    const sortedStages = mergeLeadStages(
      [...stages]?.sort((a, b) => a.position - b.position)
    );
    return (
      <Flex
        flexDir="column"
        border="1px solid #EAEAEA"
        borderRadius="6px"
        background="#FFF"
        boxShadow="0px 2px 4px rgba(0, 0, 0, 0.05)"
        _hover={{
          boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)',
          transform: 'translateY(-2px)',
        }}
        transition="all 0.3s ease-in-out"
      >
        {/* heading part */}
        <Link to={redirectLink} rel="noopener noreferrer">
          <Flex
            borderBottom="1px solid #EBEBEB"
            p={
              isNarrow
                ? '1em 1em 0.875em 1em'
                : '1.125em 1.875em 0.938em 1.875em'
            }
            justifyContent="space-between"
            cursor="pointer"
            gap="12px"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
          >
            {sectionOne()}
            {/* On a narrow card this rides the title row instead — as a sibling
              here it is a full-height column, so it reserves the right edge on
              every row below it too. */}
            {!isNarrow && sectionTwo()}
          </Flex>
        </Link>

        {/* content part */}
        <Flex w="100%">
          <JobStageCell
            {...cellProps}
            item={{
              application_count:
                totalCount + (rejected_applications_count || 0),
              name: 'Candidates',
              pos: 'first',
              id: 'all_stages',
            }}
          />

          <Box position="relative" flex="1" minW={0} data-scroll-group>
            <Flex w="100%" overflowX="auto" ref={stagesRef}>
              {sortedStages?.map((item, index) => (
                <JobStageCell
                  key={item?.id ?? index}
                  {...cellProps}
                  item={item}
                />
              ))}
              {sortedStages?.length > 0 && (
                <JobStageCell
                  {...cellProps}
                  item={{
                    application_count: rejected_applications_count,
                    name: 'Rejected',
                    pos: 'last',
                    id: 'rejected',
                  }}
                  wrapperProps={{
                    bg: 'pastel-1-light',
                    _hover: { bg: 'pastel-1-light', cursor: 'pointer' },
                  }}
                  textProps={{ color: 'pastel-1-dark' }}
                />
              )}
            </Flex>
            {SHOW_STAGE_SCROLL_ARROWS && canScrollLeft && (
              <ScrollArrow direction="left" onClick={() => scrollByPage(-1)} />
            )}
            {SHOW_STAGE_SCROLL_ARROWS && canScrollRight && (
              <ScrollArrow direction="right" onClick={() => scrollByPage(1)} />
            )}
          </Box>
        </Flex>
      </Flex>
    );
  }
);
RenderJobCard.displayName = 'RenderJobCard';

export const JobsCard = () => {
  const {
    getAllJobs: { data = {} } = {},
    page,
    currentView = {},
    searchTerm,
    filters,
    sort,
  } = useSelector(getAtsJobs);
  const { applyPage } = atsJobsActions;
  const { ats_jobs = [], pagination = {} } = data;
  const dispatch = useDispatch();
  const cardContainerRef = useRef(null);
  // Measured once for the grid. Per-card it attached one ResizeObserver per row
  // to the same element, so a single resize fanned out to a re-render each.
  const containerWidth = useContainerWidth(cardContainerRef) || 1500;

  const refetchJobs = useCallback(() => {
    fetchJobsWithFilter({
      currentView,
      filters,
      sort,
      page,
      searchTerm,
      dispatch,
    });
  }, [currentView, filters, sort, page, searchTerm, dispatch]);

  return (
    <Flex flexDir="column" gap="lg" h="100%" w="100%" ref={cardContainerRef}>
      <Flex
        flexDir="column"
        overflow="auto"
        gap="md"
        flex="1"
        borderBottom="1px solid"
        borderColor="border-color"
      >
        {ats_jobs.map((job) => (
          <RenderJobCard
            key={job?.id}
            job={job}
            refetchJobs={refetchJobs}
            containerWidth={containerWidth}
          />
        ))}
      </Flex>
      <Flex flexShrink="0" justifyContent="flex-end">
        <Pagination
          type="scroll"
          pagination={pagination}
          number={page?.number}
          applyPage={applyPage}
        />
      </Flex>
    </Flex>
  );
};
